import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { FieldDisplayType, OptionsManagerData } from "../types";
import { getFieldDisplayTypeIcon, getFieldDisplayTypeLabel } from "../hooks";
import {
  getFieldDisplayTypeHelpText,
  generateFieldDisplayTypeOptions,
  restrictFieldTypeOptions
} from "../utils";
import {
  ADD_CUSTOM_FIELD_TRANSLATION_KEY,
  TOGGLEABLE_REQUIRED_EDITABLE_SYSTEM_FIELD_KEYS
} from "../const";
import { FieldFormData } from "./fieldFormUtils";
import { APIFieldDisplayTypeTargets, APIFieldItem } from "@/pages/settings/types";
import { FieldPopulation } from "../components/FieldPopulation";
import {
  EntityType,
  entityTypeToSingleEntityName
} from "@/services/hooks/entities/useEntityType";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { FILE_FIELD_ANY_TYPE } from "@/utils/entityFieldsUtils/fileFieldUtils";
interface FieldConfigurationData {
  configSection: FormSectionProps | null;
  configFields: any[];
  bottomCustomComponent: React.ReactNode | undefined;
}

interface UseFieldFormSectionsProps {
  formData: FieldFormData;
  optionsManagerData: OptionsManagerData;
  fieldData?: APIFieldItem | null;
  formSetValue: any;
  showHelpText: boolean;
  showInCreation: boolean;
  isRequired: boolean;
  fieldConfigurationData: FieldConfigurationData;
  onShowHelpTextChange: (show: boolean) => void;
  onShowInCreationChange: (show: boolean) => void;
  onRequiredChange: (required: boolean) => void;
  onOptionsManagerChange: (data: OptionsManagerData) => void;
  onFilledByDreamerChange?: (filledByDreamer: boolean) => void;
  isEditMode?: boolean;
  isPresetFieldEdit?: boolean;
  isEditableSystemField?: boolean;
  isWorkflowField?: boolean;
  entityType?: EntityType;
  displayTypeTargets?: APIFieldDisplayTypeTargets | null;
}

export const useFieldFormSections = ({
  formData,
  optionsManagerData,
  fieldData,
  formSetValue,
  showHelpText,
  showInCreation,
  isRequired,
  fieldConfigurationData,
  onShowHelpTextChange,
  onShowInCreationChange,
  onRequiredChange,
  onOptionsManagerChange,
  onFilledByDreamerChange,
  isEditMode = false,
  isPresetFieldEdit = false,
  isEditableSystemField = false,
  isWorkflowField = false,
  entityType = EntityType.DEAL,
  displayTypeTargets = null
}: UseFieldFormSectionsProps): FormSectionProps[] => {
  const { t } = useTranslation();
  const { enabled: isChecklistItemEnabled } = useFeatureFlag(
    FeatureFlags.CRM_7164_CHECKLIST_ITEM_FIELD_MGMT
  );
  const { enabled: isPartnerContactsFlagEnabled } = useFeatureFlag(
    FeatureFlags.CRM_8228_PARTNER_CONTACTS_TABLE
  );
  const { enabled: isFileUploadEnabled } = useFeatureFlag(
    FeatureFlags.CRM_9188_FILE_FIELD_MGMT
  );
  const partnershipsEnabled = useEnablePartnerships();
  const { enabled: configurableSystemFieldsEnabled, loading: configurableSystemFieldsLoading } = useFeatureFlag(
    FeatureFlags.CRM_5705_CONFIGURABLE_SYSTEM_FIELDS
  );
  const fieldTypeOptions = generateFieldDisplayTypeOptions(
    t,
    isWorkflowField,
    isChecklistItemEnabled,
    isPartnerContactsFlagEnabled && partnershipsEnabled,
    isFileUploadEnabled
  );

  return useMemo(() => {
    const selectedFieldDisplayType = formData.fieldType;
    const { configSection, configFields, bottomCustomComponent } =
      fieldConfigurationData;

    // The type of an existing field is editable only when the backend reports
    // safe conversion targets for it (manually populated custom fields).
    const canChangeDisplayType =
      isEditMode &&
      !isPresetFieldEdit &&
      !isEditableSystemField &&
      (displayTypeTargets?.allowedDisplayTypes?.length ?? 0) > 0;
    const isTypeSelectorDisabled = canChangeDisplayType
      ? false
      : isEditMode || isPresetFieldEdit || isEditableSystemField;

    const restrictedFieldTypeOptions = canChangeDisplayType
      ? restrictFieldTypeOptions(
          fieldTypeOptions,
          fieldData?.displayType,
          displayTypeTargets?.allowedDisplayTypes ?? [],
          t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.typeChangeBlockedPopulated`)
        )
      : isEditableSystemField &&
          fieldData?.displayType &&
          !fieldTypeOptions.some((option) => option.id === fieldData.displayType)
        ? [
            ...fieldTypeOptions,
            {
              id: fieldData.displayType,
              displayName: getFieldDisplayTypeLabel(fieldData.displayType, t),
              disabled: true
            }
          ]
        : fieldTypeOptions;

    // FormNew publishes its methods as an object, so calling formSetValue
    // directly wrote nothing and every per-type default below was dropped.
    const setFieldValue = formSetValue?.setValue;

    const handleTypeSelection = (value: string | number) => {
      if (typeof setFieldValue !== "function") {
        return;
      }
      setFieldValue("fieldType", value, { shouldValidate: true });
      const wasSelectType =
        fieldData?.displayType === FieldDisplayType.SINGLE_SELECT ||
        fieldData?.displayType === FieldDisplayType.MULTI_SELECT;
      if (
        value === FieldDisplayType.SINGLE_SELECT ||
        value === FieldDisplayType.MULTI_SELECT
      ) {
        // Keep existing options when converting between select types
        if (!(canChangeDisplayType && wasSelectType)) {
          setFieldValue("options", [""]);
          setFieldValue("allowUsersToAdd", false);
          setFieldValue("defaultValue", "none");
        }
      } else if (value === FieldDisplayType.CHECKBOX) {
        setFieldValue("defaultValue", "unchecked");
      } else if (value === FieldDisplayType.CURRENCY) {
        setFieldValue("defaultValue", "");
      } else if (value === FieldDisplayType.FILE_UPLOAD) {
        setFieldValue("supportedFiles", FILE_FIELD_ANY_TYPE, {
          shouldValidate: true
        });
        setFieldValue("allowMultipleFiles", true);
      }
    };

    const fieldTypeFields: FormFieldNewType[] = [
      {
        name: "fieldType",
        type: "singleSelect" as const,
        label: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.fieldType`),
        required: true,
        defaultValue: isEditMode ? fieldData?.displayType : undefined,
        helpText: getFieldDisplayTypeHelpText(selectedFieldDisplayType, t),
        fieldActionProps: {
          fieldName: "fieldType",
          options: restrictedFieldTypeOptions,
          placeholder: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.selectFieldType`),
          renderIcon: (value: string | number) =>
            getFieldDisplayTypeIcon(value as FieldDisplayType),
          disabled: isTypeSelectorDisabled,
          onSelection: isTypeSelectorDisabled ? () => {} : handleTypeSelection
        }
      },
      ...configFields
    ];

    const fieldLabelFields: FormFieldNewType[] = [
      {
        name: "fieldLabel",
        type: "text" as const,
        label: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.fieldLabel`),
        required: true,
        defaultValue: isEditMode ? fieldData?.label : undefined,
        fieldActionProps: {
          placeholder: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.addFieldLabel`),
          disabled: isEditableSystemField
        }
      }
    ];

    if (showHelpText || !!fieldData?.helpText) {
      fieldLabelFields.push({
        name: "fieldHelpText",
        type: "text" as const,
        label: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.fieldHelpText`),
        required: false,
        defaultValue: isEditMode ? fieldData?.helpText || "" : "",
        helpText: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.addFieldHelpText`),
        fieldActionProps: {
          placeholder: ""
        }
      });
    }

    const populationSection: FormSectionProps = {
      id: "population",
      divider: true,
      bottomCustomComponent: fieldData
        ? React.createElement(FieldPopulation, {
            fieldData,
            onFilledByDreamerChange: onFilledByDreamerChange || (() => {})
          })
        : undefined
    };
    const isToggleableRequiredEditableSystemField =
      isEditableSystemField &&
      TOGGLEABLE_REQUIRED_EDITABLE_SYSTEM_FIELD_KEYS.includes(fieldData?.fieldKey || "");

    const baseSections: FormSectionProps[] = [
      {
        id: "field-label",
        divider: true,
        sectionClassName: "gap-6",
        dividerClassName: "my-2",
        fields: fieldLabelFields,
        bottomCustomComponent:
          !showHelpText && !fieldData?.helpText && !isEditableSystemField
            ? "addHelpTextButton"
            : undefined
      },
      ...(isPresetFieldEdit && !isEditableSystemField
        ? [populationSection]
        : []),
      {
        id: "field-type",
        divider: true,
        sectionClassName: configSection?.sectionClassName || "gap-6",
        fieldsContainerClassName: configSection?.fieldsContainerClassName,
        dividerClassName: "my-2",
        fields: fieldTypeFields,
        bottomCustomComponent,
        // Merge only for editable system fields; regular select fields keep the
        // sibling render (pre-feature layout).
        mergeBottomComponent: isEditableSystemField && !!bottomCustomComponent
      },
      ...(isPresetFieldEdit || isWorkflowField || (isEditableSystemField && (!configurableSystemFieldsEnabled || configurableSystemFieldsLoading))
        ? []
        : [
            {
              id: "field-checkboxes",
              divider: false,
              fieldsContainerClassName: "gap-2",
              fields: [
                {
                  name: "showInCreation",
                  type: "checkbox" as const,
                  label: "",
                  required: false,
                  fieldContainerClassName: "min-h-8",
                  fieldActionProps: {
                    placeholder: "",
                    // System fields: always shown at creation, locked.
                    isChecked: isEditableSystemField || showInCreation,
                    disabled: isEditableSystemField,
                    onCheckedChange: (checked: boolean) => onShowInCreationChange(checked),
                    label: t(
                      `${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.showInCreation`,
                      { entityType: entityTypeToSingleEntityName[entityType] }
                    )
                  }
                },
                {
                  name: "isRequired",
                  type: "checkbox" as const,
                  label: "",
                  required: false,
                  fieldContainerClassName: "min-h-8",
                  fieldActionProps: {
                    placeholder: "",
                    // Industry locked on; Sub-Industry/phone/country toggleable; custom gated on show-in-creation.
                    isChecked:
                      (isEditableSystemField && !isToggleableRequiredEditableSystemField) ||
                      isRequired,
                    disabled: isEditableSystemField
                      ? !isToggleableRequiredEditableSystemField
                      : !showInCreation,
                    onCheckedChange: (checked: boolean) => onRequiredChange(checked),
                    label: t(
                      `${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.setAsRequired`
                    )
                  }
                }
              ]
            }
          ])
    ];

    return baseSections;
  }, [
    formData,
    optionsManagerData,
    fieldData,
    fieldTypeOptions,
    formSetValue,
    fieldConfigurationData,
    showHelpText,
    showInCreation,
    isRequired,
    isEditMode,
    isPresetFieldEdit,
    isEditableSystemField,
    isWorkflowField,
    configurableSystemFieldsEnabled,
    configurableSystemFieldsLoading,
    displayTypeTargets,
    onShowHelpTextChange,
    onShowInCreationChange,
    onRequiredChange,
    onOptionsManagerChange,
    onFilledByDreamerChange,
    t
  ]);
};
